import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NuevoTramiteModal from '../components/NuevoTramiteModal';
import DetalleTramiteModal from '../components/DetalleTramiteModal';
import EditarTramiteModal from '../components/EditarTramiteModal';
import { AuthContext } from '../context/AuthContext';
import TramiteDeleteModal from '../components/TramiteDeleteModal';
import TramiteRequestDeletionModal from '../components/TramiteRequestDeletionModal';
import Pagination from '../components/Pagination';
import NovedadesTramiteModal from '../components/NovedadesTramiteModal';
import VerificarSoatsModal from '../components/VerificarSoatsModal';
import AlertaDocumentosPreasignacion from '../components/AlertaDocumentosPreasignacion';
import * as XLSX from 'xlsx';
import { generateTramiteExcelRow } from '../utils/exportExcelUtils';
import './Profiles.css'; // Reutilizamos estilos base

const normalizeText = (str) => {
  if (!str) return '';
  return str
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
};

const getUniqueOptions = (list, keyExtractor) => {
  const map = new Map();
  (list || []).forEach(item => {
    const rawVal = keyExtractor(item);
    if (!rawVal) return;
    const normKey = normalizeText(rawVal);
    if (!map.has(normKey)) {
      map.set(normKey, rawVal.trim());
    }
  });
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
};

const MisTramites = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [tramites, setTramites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Estado para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Estado para el modal de detalle
  const [selectedTramiteId, setSelectedTramiteId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Estado para el modal de edición
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTramiteForEdit, setSelectedTramiteForEdit] = useState(null);

  // Estado para eliminación de trámites
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTramiteToDelete, setSelectedTramiteToDelete] = useState(null);

  // Estado para solicitud de eliminación (concesionario)
  const [isRequestDeleteModalOpen, setIsRequestDeleteModalOpen] = useState(false);
  const [selectedTramiteToRequestDelete, setSelectedTramiteToRequestDelete] = useState(null);

  // Estado para el modal de novedades
  const [isNovedadesModalOpen, setIsNovedadesModalOpen] = useState(false);
  const [novedadesTramiteId, setNovedadesTramiteId] = useState(null);
  const [showVerificarSoatsModal, setShowVerificarSoatsModal] = useState(false);

  // Estados para filtros de búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('todos');
  const [filterType, setFilterType] = useState('todos');
  const [filterNovedad, setFilterNovedad] = useState(false);
  const [filterFactura, setFilterFactura] = useState(false);
  const [filterOcultarEjecutados, setFilterOcultarEjecutados] = useState(false);
  const [filterConcesionario, setFilterConcesionario] = useState('todos');
  const [filterTransito, setFilterTransito] = useState('todos');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  useEffect(() => {
    fetchTramites();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = location.state?.openTramiteId || params.get('openTramiteId');
    if (openId) {
      setSelectedTramiteId(parseInt(openId));
      setIsDetailModalOpen(true);
    }
  }, [location]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterEstado, filterType, filterNovedad, filterFactura, filterOcultarEjecutados, filterConcesionario, filterTransito, fechaInicio, fechaFin]);

  const fetchTramites = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/tramites');
      // Ahora res.data tiene estructura: { data: [...], pagination: {...} }
      const tramitesArray = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setTramites(tramitesArray);
      setError('');
    } catch (err) {
      setError('Error al cargar tus trámites');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      'radicado': { label: '📝 Radicado', class: 'pending' },
      'pre_asignado': { label: '🚗 Pre-Asignado', class: 'activo' },
      'novedad_pendiente_soat': { label: '⚠️ SOAT Pendiente', class: 'warning' },
      'novedad_soat_cargado': { label: '📄 SOAT Cargado', class: 'warning' },
      'novedad_soat_vigente': { label: '✅ SOAT Vigente', class: 'paid' },
      'novedad_pendiente_cupl': { label: '⚠️ CUPL Pendiente', class: 'warning' },
      'novedad_cupl_generado': { label: '📄 CUPL Generado', class: 'warning' },
      'novedad_cupl_pagado': { label: '💳 CUPL Pagado', class: 'paid' },
      'tramite_ejecutado': { label: '🔒 Ejecutado', class: 'paid' },
      'placa_en_camino': { label: '🚚 Placa en Camino', class: 'warning' },
      'placa_entregada': { label: '🏪 Placa Entregada', class: 'warning' },
      'placa_confirmada': { label: '✅ Entregada y Confirmada', class: 'paid' },
      'incidencia_placa': { label: '⚠️ Incidencia Placa', class: 'expired' },
      'rechazado': { label: '❌ Rechazado', class: 'expired' }
    };
    const s = statuses[status] || { label: status, class: '' };
    return <span className={`status ${s.class}`}>{s.label}</span>;
  };

  const openDetalle = (id) => {
    setSelectedTramiteId(id);
    setIsDetailModalOpen(true);
  };

  const openEdit = (id) => {
    setSelectedTramiteForEdit(id);
    setIsEditModalOpen(true);
  };

  const openDeleteConfirm = (tramite) => {
    setSelectedTramiteToDelete(tramite);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedTramiteToDelete) return;
    try {
      await axios.delete(`/api/tramites/${selectedTramiteToDelete.id}`);
      setIsDeleteModalOpen(false);
      setSelectedTramiteToDelete(null);
      fetchTramites();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al eliminar el trámite');
    }
  };

  const openRequestDelete = (tramite) => {
    setSelectedTramiteToRequestDelete(tramite);
    setIsRequestDeleteModalOpen(true);
  };

  const handleRequestDeleteSubmit = async (reason) => {
    if (!selectedTramiteToRequestDelete) return;
    try {
      await axios.post(`/api/tramites/${selectedTramiteToRequestDelete.id}/request-deletion`, { reason });
      setIsRequestDeleteModalOpen(false);
      setSelectedTramiteToRequestDelete(null);
      fetchTramites();
      alert('Solicitud de eliminación enviada exitosamente al tránsito.');
    } catch (err) {
      alert(err.response?.data?.message || 'Error al solicitar la eliminación del trámite');
    }
  };

  const handleEjecutarTramite = async (id) => {
    if (!window.confirm('¿Está seguro de finalizar y ejecutar este trámite? Esta acción es definitiva.')) return;
    try {
      await axios.put(`/api/tramites/${id}/estado`, { estado: 'tramite_ejecutado' });
      fetchTramites();
    } catch (err) {
      alert(err.response?.data?.message || 'Error al ejecutar el trámite');
    }
  };

  const exportToExcel = () => {
    if (filteredTramites.length === 0) {
      alert('No hay datos para exportar con los filtros actuales.');
      return;
    }

    const exportData = filteredTramites.map(t => generateTramiteExcelRow(t, user, filterEstado));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Trámites');
    XLSX.writeFile(workbook, `Mis_Tramites_${new Date().toLocaleDateString('es-CO')}.xlsx`);
  };

  // Lógica de filtrado en memoria
  const filteredTramites = tramites.filter(t => {
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      const idMatch = t.id.toString().includes(term);
      const placaMatch = (t.datos_adicionales?.placa_referencia || '').toLowerCase().includes(term);
      const ownerMatch = (t.datos_adicionales?.propietario || '').toLowerCase().includes(term);
      const creatorMatch = (t.creador_name || '').toLowerCase().includes(term);
      const pvMatch = (t.punto_venta_name || t.datos_adicionales?.punto_venta_factura || t.datos_adicionales?.punto_venta_nombre || t.datos_adicionales?.punto_venta || '').toLowerCase().includes(term);
      const asesorMatch = (t.datos_adicionales?.asesor || t.datos_adicionales?.vendedor_factura || '').toLowerCase().includes(term);
      if (!idMatch && !placaMatch && !ownerMatch && !creatorMatch && !pvMatch && !asesorMatch) return false;
    }

    if (filterEstado !== 'todos') {
      if (filterEstado === 'pre_asignado') {
        if (t.estado !== 'pre_asignado') return false;
      } else if (filterEstado === 'novedad_soat_vigente') {
        if (!(t.datos_adicionales?.soat_aprobado === true || t.estado === filterEstado)) return false;
      } else if (filterEstado === 'novedad_soat_cargado') {
        if (!(t.datos_adicionales?.soat_cargado === true || t.estado === filterEstado)) return false;
      } else if (filterEstado === 'novedad_cupl_generado') {
        if (!(t.datos_adicionales?.cupl_cargado === true || t.estado === filterEstado)) return false;
      } else if (filterEstado === 'novedad_cupl_pagado') {
        if (!(t.datos_adicionales?.cupl_pagado === true || t.estado === filterEstado)) return false;
      } else if (filterEstado === 'tramite_ejecutado') {
        if (!['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado)) return false;
      } else {
        if (t.estado !== filterEstado) return false;
      }
    }

    if (filterType !== 'todos') {
      if (normalizeText(t.tramite_type_name) !== normalizeText(filterType)) return false;
    }

    if (filterConcesionario !== 'todos') {
      if (normalizeText(t.creador_concesionario_name) !== normalizeText(filterConcesionario)) return false;
    }

    if (filterTransito !== 'todos') {
      if (normalizeText(t.transito_name) !== normalizeText(filterTransito)) return false;
    }


    if (filterNovedad) {
      if (!(t.pending_novelties_count > 0)) return false;
    }

    if (filterFactura) {
      if (t.factura_count !== 0 || t.datos_adicionales?.tipo_flujo === 'otros_tramites') return false;
    }
    
    if (filterOcultarEjecutados) {
      if (['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado)) {
        return false;
      }
    }

    if (fechaInicio) {
      let dateToCompare = t.created_at;
      if (filterEstado !== 'todos') {
        if (filterEstado === 'tramite_ejecutado') {
          if (!t.fecha_ejecutado) return false;
          dateToCompare = t.fecha_ejecutado;
        } else if (filterEstado === 'pre_asignado') {
          if (!t.fecha_preasignacion) return false;
          dateToCompare = t.fecha_preasignacion;
        } else {
          dateToCompare = t.updated_at || t.created_at;
        }
      }
      const trDate = new Date(dateToCompare).setHours(0,0,0,0);
      const start = new Date(`${fechaInicio}T00:00:00`).setHours(0,0,0,0);
      if (trDate < start) return false;
    }
    
    if (fechaFin) {
      let dateToCompare = t.created_at;
      if (filterEstado !== 'todos') {
        if (filterEstado === 'tramite_ejecutado') {
          if (!t.fecha_ejecutado) return false;
          dateToCompare = t.fecha_ejecutado;
        } else if (filterEstado === 'pre_asignado') {
          if (!t.fecha_preasignacion) return false;
          dateToCompare = t.fecha_preasignacion;
        } else {
          dateToCompare = t.updated_at || t.created_at;
        }
      }
      const trDate = new Date(dateToCompare).setHours(0,0,0,0);
      const end = new Date(`${fechaFin}T00:00:00`).setHours(0,0,0,0);
      if (trDate > end) return false;
    }

    return true;
  });

  // Lógica de paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTramites.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="📁 Mis Trámites" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          {/* Placas pre-asignadas por el tránsito: el concesionario debe descargar el FUN y el contrato de mandato */}
          {!user?.transito_id && user?.role !== 'transito' && <AlertaDocumentosPreasignacion />}

          <div className="page-header">
            <div className="header-title">
              <h2>Listado de Solicitudes</h2>
              <p className="subtitle">Seguimiento en tiempo real de tus trámites iniciados</p>
            </div>
            {user?.profile_name !== 'auxiliar transiapp' && !['auxiliar_concesionario', 'administrativo_concesionario'].includes(user?.profile_name) && (
              <button onClick={() => setIsModalOpen(true)} className="btn-primary">
                ➕ Nuevo Trámite
              </button>
            )}
          </div>

          {/* Barra de Filtros */}
          <div className="dashboard-filters" style={{ 
            display: 'flex', 
            gap: '15px', 
            background: 'var(--card-bg)', 
            padding: '16px 24px', 
            borderRadius: '14px', 
            alignItems: 'center', 
            flexWrap: 'wrap',
            boxShadow: 'var(--card-shadow)',
            border: '1px solid var(--card-border)',
            marginBottom: '20px'
          }}>
            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px' }}>
              <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Buscar:</label>
              <input
                type="text"
                placeholder="ID, Placa, Propietario o Creador..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ 
                  padding: '10px 14px', 
                  border: '1px solid var(--input-border)', 
                  borderRadius: '10px', 
                  background: 'var(--input-bg)', 
                  color: 'var(--text-primary)', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>

            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
              <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Estado:</label>
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                style={{ 
                  padding: '10px 14px', 
                  border: '1px solid var(--input-border)', 
                  borderRadius: '10px', 
                  background: 'var(--input-bg)', 
                  color: 'var(--text-primary)', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="todos">📋 Todos los Estados</option>
                <option value="radicado">📝 Radicado</option>
                <option value="pre_asignado">🚗 Pre-Asignado</option>
                <option value="novedad_pendiente_soat">📄 SOAT Pendiente</option>
                <option value="novedad_soat_cargado">🛡️ SOAT Cargado</option>
                <option value="novedad_soat_vigente">🛡️ SOAT Vigente</option>
                <option value="novedad_pendiente_cupl">💳 CUPL Pendiente</option>
                <option value="novedad_cupl_generado">📄 CUPL Generado</option>
                <option value="novedad_cupl_pagado">💳 CUPL Pagado</option>
                <option value="tramite_ejecutado">🎉 Trámite Ejecutado</option>
                <option value="placa_en_camino">🚚 Placa en Camino</option>
                <option value="placa_entregada">🏪 Placa Entregada</option>
                <option value="placa_confirmada">✅ Placa Confirmada</option>
                <option value="incidencia_placa">⚠️ Incidencia Placa</option>
              </select>
            </div>

            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
              <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tipo de Trámite:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={{ 
                  padding: '10px 14px', 
                  border: '1px solid var(--input-border)', 
                  borderRadius: '10px', 
                  background: 'var(--input-bg)', 
                  color: 'var(--text-primary)', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="todos">🔧 Todos los Tipos</option>
                {getUniqueOptions(tramites, t => t.tramite_type_name).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            {(user?.role === 'superadmin' || user?.profile_name === 'superadmin') && (
              <>
                <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Concesionario:</label>
                  <select
                    value={filterConcesionario}
                    onChange={(e) => setFilterConcesionario(e.target.value)}
                    style={{ 
                      padding: '10px 14px', 
                      border: '1px solid var(--input-border)', 
                      borderRadius: '10px', 
                      background: 'var(--input-bg)', 
                      color: 'var(--text-primary)', 
                      fontSize: '13px', 
                      fontWeight: '500', 
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="todos">🏢 Todos los Concesionarios</option>
                    {getUniqueOptions(tramites, t => t.creador_concesionario_name).map(conc => (
                      <option key={conc} value={conc}>{conc}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '180px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tránsito:</label>
                  <select
                    value={filterTransito}
                    onChange={(e) => setFilterTransito(e.target.value)}
                    style={{ 
                      padding: '10px 14px', 
                      border: '1px solid var(--input-border)', 
                      borderRadius: '10px', 
                      background: 'var(--input-bg)', 
                      color: 'var(--text-primary)', 
                      fontSize: '13px', 
                      fontWeight: '500', 
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="todos">🏛️ Todos los Tránsitos</option>
                    {getUniqueOptions(tramites, t => t.transito_name).map(tr => (
                      <option key={tr} value={tr}>{tr}</option>
                    ))}
                  </select>
                </div>
              </>
            )}


            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '135px' }}>
              <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Desde:</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                style={{ 
                  padding: '10px 14px', 
                  border: '1px solid var(--input-border)', 
                  borderRadius: '10px', 
                  background: 'var(--input-bg)', 
                  color: 'var(--text-primary)', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>

            <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '135px' }}>
              <label style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Hasta:</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                style={{ 
                  padding: '10px 14px', 
                  border: '1px solid var(--input-border)', 
                  borderRadius: '10px', 
                  background: 'var(--input-bg)', 
                  color: 'var(--text-primary)', 
                  fontSize: '13px', 
                  fontWeight: '500', 
                  outline: 'none',
                  width: '100%'
                }}
              />
            </div>

            <div className="checkbox-filters" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '10px' }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: 'var(--text-primary)', 
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={filterNovedad}
                  onChange={(e) => setFilterNovedad(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                ⚠️ Con Novedades
              </label>

              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: 'var(--text-primary)', 
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={filterFactura}
                  onChange={(e) => setFilterFactura(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                ⚠️ Sin Factura
              </label>

              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                fontSize: '13px', 
                fontWeight: '600', 
                color: 'var(--text-primary)', 
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={filterOcultarEjecutados}
                  onChange={(e) => setFilterOcultarEjecutados(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                />
                🙈 Ocultar Ejecutados
              </label>
            </div>

            <button 
              onClick={() => {
                setSearchTerm('');
                setFilterEstado('todos');
                setFilterType('todos');
                setFilterConcesionario('todos');
                setFilterTransito('todos');
                setFilterNovedad(false);
                setFilterFactura(false);
                setFilterOcultarEjecutados(false);
                setFechaInicio('');
                setFechaFin('');
              }}
              style={{
                marginLeft: 'auto',
                padding: '10px 16px',
                background: 'var(--secondary-soft)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--card-border)',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '13px',
                marginTop: '15px',
                transition: 'all 0.2s'
              }}
            >
              🧹 Limpiar
            </button>
            <button 
              onClick={exportToExcel}
              style={{
                padding: '10px 16px',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '13px',
                marginTop: '15px',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              📥 Excel
            </button>
          </div>

          {loading ? (
            <p className="loading">Cargando trámites...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tipo de Trámite</th>
                    <th>Tránsito</th>
                    <th>Propietario</th>
                    {(!user?.transito_id && user?.role !== 'transito') && <th>Solicitante</th>}
                    <th>Placa</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(t => (
                    <tr key={t.id} style={{ background: t.deletion_requested ? '#fee2e2' : '' }}>
                      <td data-label="ID">
                        <strong>#{t.id}</strong>
                        {t.deletion_requested && (
                          <div style={{ marginTop: '4px', fontSize: '10px', color: '#ef4444', fontWeight: 'bold' }}>
                            ⚠️ MARCADO PARA ELIMINACIÓN
                          </div>
                        )}
                      </td>
                      <td data-label="Tipo de Trámite">{t.tramite_type_name}</td>
                      <td data-label="Tránsito">{t.transito_name}</td>
                      <td data-label="Propietario">
                        <div><strong>{t.datos_adicionales?.propietario || 'N/A'}</strong></div>
                        {(t.datos_adicionales?.observaciones || t.observaciones) && (
                          <div style={{ fontSize: '11px', color: '#0284c7', marginTop: '3px', fontStyle: 'italic' }} title={t.datos_adicionales?.observaciones || t.observaciones}>
                            💬 {t.datos_adicionales?.observaciones || t.observaciones}
                          </div>
                        )}
                      </td>
                      {(!user?.transito_id && user?.role !== 'transito') && (
                        <td data-label="Solicitante">
                          <strong>{t.creador_name}</strong>
                          {(t.punto_venta_name || t.datos_adicionales?.punto_venta_factura || t.datos_adicionales?.punto_venta_nombre || t.datos_adicionales?.punto_venta) ? (
                            <div style={{ fontSize: '11px', color: '#7c3aed', marginTop: '3px', fontWeight: '700' }}>
                              📍 {t.punto_venta_name || t.datos_adicionales?.punto_venta_factura || t.datos_adicionales?.punto_venta_nombre || t.datos_adicionales?.punto_venta}
                            </div>
                          ) : (
                            t.creador_concesionario_name || t.creador_transito_name ? (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                🏢 {t.creador_concesionario_name || t.creador_transito_name}
                              </div>
                            ) : (
                              <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                                💼 Independiente
                              </div>
                            )
                          )}
                          {(t.datos_adicionales?.asesor || t.datos_adicionales?.vendedor_factura) && (
                            <div style={{ fontSize: '10px', color: '#475569', marginTop: '2px', fontStyle: 'italic' }}>
                              👤 Asesor: {t.datos_adicionales.asesor || t.datos_adicionales.vendedor_factura}
                            </div>
                          )}
                        </td>
                      )}
                      <td data-label="Placa">
                        {t.datos_adicionales?.placa_referencia ? (
                          <span className="plate-badge-sm">{t.datos_adicionales.placa_referencia}</span>
                        ) : '—'}
                        {t.pending_plate_change_number && (
                          <div style={{ marginTop: '4px' }}>
                            <span style={{ background: '#f59e0b', color: '#fff', fontSize: '10px', padding: '2px 6px', borderRadius: '8px', fontWeight: '800', display: 'inline-block' }}>
                              ⏳ Solicitada: {t.pending_plate_change_number}
                            </span>
                          </div>
                        )}
                      </td>
                      <td data-label="Estado">
                        {getStatusBadge(t.estado)}
                        {t.factura_count === 0 && t.datos_adicionales?.tipo_flujo !== 'otros_tramites' && (
                          <div style={{ marginTop: '5px' }}>
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '900',
                              background: '#fff7ed',
                              color: '#ea580c',
                              border: '1px solid rgba(234, 88, 12, 0.2)',
                              whiteSpace: 'nowrap'
                            }} title="Este trámite se creó sin factura adjunta">
                              ⚠️ SIN FACTURA
                            </span>
                          </div>
                        )}
                      </td>
                      <td data-label="Fecha">{new Date(t.created_at).toLocaleDateString()}</td>
                      <td data-label="Acciones" className="actions">
                        <button 
                          className="btn-action btn-view" 
                          onClick={() => openDetalle(t.id)}
                        >
                          👁️ Ver Detalle
                        </button>
                        {user?.profile_name !== 'operador' && user?.profile_name !== 'auxiliar transiapp' && !['auxiliar_concesionario', 'administrativo_concesionario'].includes(user?.profile_name) && (
                          <>
                            {t.pending_novelties_count > 0 ? (
                              <button 
                                className="btn-action btn-warning" 
                                onClick={() => {
                                  setNovedadesTramiteId(t.id);
                                  setIsNovedadesModalOpen(true);
                                }} 
                                title={`Este trámite tiene ${t.pending_novelties_count} novedades pendientes`}
                                style={{
                                  background: 'rgba(245, 158, 11, 0.1)',
                                  color: '#d97706',
                                  border: '1px solid rgba(245, 158, 11, 0.2)',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  marginLeft: '5px',
                                  transition: 'all 0.25s'
                                }}
                              >
                                ⚠️ Novedad ({t.pending_novelties_count})
                              </button>
                            ) : (
                              <button 
                                className="btn-action" 
                                onClick={() => {
                                  setNovedadesTramiteId(t.id);
                                  setIsNovedadesModalOpen(true);
                                }} 
                                style={{ 
                                  background: 'rgba(71, 85, 105, 0.1)', 
                                  color: '#475569', 
                                  border: '1px solid rgba(71, 85, 105, 0.2)', 
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  marginLeft: '5px',
                                  transition: 'all 0.25s'
                                }}
                              >
                                📋 Novedades
                              </button>
                            )}
                            <button 
                              className="btn-action btn-edit" 
                              onClick={() => openEdit(t.id)}
                              disabled={['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado)}
                              title={['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado) ? "No se puede editar: trámite ya finalizado" : "Editar Trámite"}
                              style={{
                                background: ['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado) ? 'rgba(241, 245, 249, 0.5)' : 'rgba(37, 99, 235, 0.1)',
                                color: ['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado) ? '#94a3b8' : '#2563eb',
                                border: ['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado) ? '1px solid #e2e8f0' : '1px solid rgba(37, 99, 235, 0.2)',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                cursor: ['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado) ? 'not-allowed' : 'pointer',
                                marginLeft: '5px',
                                transition: 'all 0.2s'
                              }}
                            >
                              ✏️ Editar
                            </button>
                            {((user?.role === 'superadmin' || !!user?.transito_id || user?.role === 'transito') && t.datos_adicionales?.soat_aprobado === true && t.datos_adicionales?.cupl_pagado === true && !['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado) && (!t.pending_novelties_count || t.pending_novelties_count === 0) && (t.factura_count > 0 || t.datos_adicionales?.tipo_flujo === 'otros_tramites')) && (
                              <button 
                                className="btn-action" 
                                style={{ 
                                  background: 'rgba(37, 99, 235, 0.1)', 
                                  color: '#2563eb', 
                                  border: '1px solid rgba(37, 99, 235, 0.2)',
                                  cursor: 'pointer',
                                  marginLeft: '5px',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  fontWeight: '600'
                                }}
                                title="Finalizar y ejecutar trámite"
                                onClick={() => handleEjecutarTramite(t.id)}
                              >
                                🔒 Finalizar
                              </button>
                            )}
                          </>
                        )}
                        {(user?.concesionario_id && !['auxiliar_concesionario', 'administrativo_concesionario'].includes(user?.profile_name) && !['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado)) && (
                          <button 
                            className="btn-action" 
                            onClick={() => openRequestDelete(t)}
                            disabled={t.deletion_requested}
                            style={{ 
                              background: t.deletion_requested ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.1)', 
                              color: t.deletion_requested ? '#b91c1c' : '#ef4444', 
                              border: t.deletion_requested ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid rgba(239, 68, 68, 0.2)',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              cursor: t.deletion_requested ? 'not-allowed' : 'pointer',
                              marginLeft: '5px',
                              transition: 'all 0.2s'
                            }}
                            title={t.deletion_requested ? "Ya se solicitó la eliminación" : "Solicitar Eliminación"}
                          >
                            🗑️ {t.deletion_requested ? 'Solicitado' : 'Eliminar'}
                          </button>
                        )}
                        {(user?.role === 'superadmin' && !['tramite_ejecutado', 'placa_en_camino', 'placa_entregada', 'placa_confirmada', 'incidencia_placa'].includes(t.estado)) && (
                          <button 
                            className="btn-action" 
                            onClick={() => openDeleteConfirm(t)}
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              color: '#ef4444', 
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              marginLeft: '5px',
                              transition: 'all 0.2s'
                            }}
                            title="Eliminar Trámite (Permanente)"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {filteredTramites.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No hay trámites que coincidan con los filtros seleccionados.
                </div>
              )}

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredTramites.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      <NuevoTramiteModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSuccess={fetchTramites} 
      />

      <DetalleTramiteModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        tramiteId={selectedTramiteId}
        onUpdate={fetchTramites}
      />

      <NovedadesTramiteModal 
        isOpen={isNovedadesModalOpen}
        onClose={() => {
          setIsNovedadesModalOpen(false);
          setNovedadesTramiteId(null);
        }}
        tramiteId={novedadesTramiteId}
        onSuccess={fetchTramites}
        readOnly={user?.role !== 'superadmin' && user?.role !== 'admin' && !user?.transito_id && !(user?.concesionario_id && !['auxiliar_concesionario', 'administrativo_concesionario'].includes(user?.profile_name) && user?.profile_name !== 'auxiliar transiapp')}
      />

      <EditarTramiteModal
        isOpen={isEditModalOpen}
        tramiteId={selectedTramiteForEdit}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedTramiteForEdit(null);
        }}
        onSuccess={fetchTramites}
      />

      <TramiteDeleteModal
        isOpen={isDeleteModalOpen}
        tramite={selectedTramiteToDelete}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <TramiteRequestDeletionModal
        isOpen={isRequestDeleteModalOpen}
        tramite={selectedTramiteToRequestDelete}
        onClose={() => setIsRequestDeleteModalOpen(false)}
        onConfirm={handleRequestDeleteSubmit}
      />

      <VerificarSoatsModal
        isOpen={showVerificarSoatsModal}
        onClose={() => setShowVerificarSoatsModal(false)}
        onVerificacionComplete={fetchTramites}
      />
    </div>
  );
};

export default MisTramites;
